<template>
  <div class="bom-import-review">
    <div v-if="newItems.length" class="bom-import-section">
      <div class="section-title">一、新增部件（{{ newItems.length }}）</div>
      <p class="section-hint">库中无匹配，以下为大模型从 BOM 抽取的参考信息，确认写入后将入库。</p>
      <div class="mini-table-wrap">
        <table class="mini-table">
          <thead>
            <tr>
              <th>编码</th>
              <th>名称</th>
              <th>规格型号</th>
              <th>供应商</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in newItems.slice(0, 12)" :key="'n-' + idx">
              <td>{{ row.code || '—' }}</td>
              <td>{{ row.name || '—' }}</td>
              <td>{{ row.specModel || row.specSummary || '—' }}</td>
              <td>{{ row.supplier || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="newItems.length > 12" class="more-hint">另有 {{ newItems.length - 12 }} 条未展开…</p>
      </div>
    </div>

    <div v-if="overlapIdentical.length" class="bom-import-section">
      <div class="section-title">完全相同（{{ overlapIdentical.length }}，无需写入）</div>
      <ul class="identical-list">
        <li v-for="(row, idx) in overlapIdentical.slice(0, 8)" :key="'i-' + idx">
          BOM {{ row.bomCode || '—' }} ↔ 库 {{ row.catalogCode || '—' }}
          <span v-if="row.note" class="muted">（{{ row.note }}）</span>
        </li>
      </ul>
    </div>

    <div v-if="overlapConflicts.length" class="bom-import-section">
      <div class="section-title">二、重叠部件（{{ overlapConflicts.length }}，请逐条确认）</div>
      <p class="section-hint">编码或名称与库内一致，但规格/供应商等信息不一致。请选择处理方式。</p>
      <div class="mini-table-wrap">
        <table class="mini-table conflict-table">
          <thead>
            <tr>
              <th>BOM 编码</th>
              <th>BOM 名称</th>
              <th>库内编码</th>
              <th>冲突说明</th>
              <th>处理</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in overlapConflicts" :key="row.conflictId">
              <td>{{ row.bomItem?.code || '—' }}</td>
              <td>{{ row.bomItem?.name || '—' }}</td>
              <td>{{ row.catalogCode || '—' }}</td>
              <td class="reason-cell">{{ row.conflictReason || '—' }}</td>
              <td>
                <select
                  class="action-select"
                  :value="decisions[row.conflictId] || 'skip'"
                  @change="onDecision(row.conflictId, ($event.target as HTMLSelectElement).value)"
                >
                  <option value="skip">跳过</option>
                  <option value="use_existing">更新库内部件</option>
                  <option value="create_new">
                    新建（{{ row.suggestedNewCode || '自动编码' }}）
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="similarItems.length" class="bom-import-section">
      <div class="section-title">三、相似部件对比（{{ similarItems.length }}）</div>
      <p class="section-hint">规格/尺寸相近但价格、供应商等不一致；已标注更优选项（仅供参考，不自动写入）。</p>
      <div
        v-for="row in similarItems"
        :key="row.similarId"
        class="similar-card"
        :class="{ 'similar-card--bom': row.bomBetter }"
      >
        <div class="similar-head">
          <span class="better-tag" :class="row.bomBetter ? 'better-tag--bom' : 'better-tag--catalog'">
            {{ row.betterLabel || (row.bomBetter ? 'BOM 更优' : '库内更优') }}
          </span>
          <span class="similar-pair">
            BOM {{ row.bomItem?.code || '—' }} / {{ row.bomItem?.name || '—' }}
            ↔ 库 {{ row.catalogCode || '—' }} / {{ row.catalogName || '—' }}
          </span>
        </div>
        <p class="similar-comparison">{{ row.comparison || '—' }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  ComponentBomImportItem,
  ComponentBomImportOverlapConflict,
  ComponentBomImportOverlapIdentical,
  ComponentBomImportSimilar,
} from '@/api/componentBomImportApi';

const props = defineProps<{
  newItems: ComponentBomImportItem[];
  overlapIdentical: ComponentBomImportOverlapIdentical[];
  overlapConflicts: ComponentBomImportOverlapConflict[];
  similarItems: ComponentBomImportSimilar[];
  decisions: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: 'update:decisions', v: Record<string, string>): void;
}>();

function onDecision(conflictId: string, action: string) {
  emit('update:decisions', { ...props.decisions, [conflictId]: action });
}
</script>

<style scoped>
.bom-import-review {
  margin-top: 8px;
  font-size: 12px;
  color: #333;
}
.bom-import-section {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
.bom-import-section:last-child {
  border-bottom: none;
}
.section-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
}
.section-hint {
  margin: 0 0 8px;
  color: #666;
  line-height: 1.45;
}
.mini-table-wrap {
  overflow-x: auto;
}
.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.mini-table th,
.mini-table td {
  border: 1px solid #e8e8e8;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.mini-table th {
  background: #f5f6f7;
  font-weight: 600;
}
.reason-cell {
  max-width: 140px;
}
.action-select {
  max-width: 160px;
  font-size: 11px;
}
.identical-list {
  margin: 0;
  padding-left: 18px;
  color: #666;
}
.identical-list .muted {
  color: #999;
}
.more-hint {
  margin: 4px 0 0;
  color: #999;
}
.similar-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 8px;
  background: #fafafa;
}
.similar-card--bom {
  border-color: #b7eb8f;
  background: #f6ffed;
}
.similar-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.better-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}
.better-tag--bom {
  background: #52c41a;
  color: #fff;
}
.better-tag--catalog {
  background: #1890ff;
  color: #fff;
}
.similar-pair {
  flex: 1;
  min-width: 120px;
}
.similar-comparison {
  margin: 0;
  line-height: 1.5;
  color: #444;
}
</style>
